import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {first, startWith, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {ThresholdDictionary} from '../../interface/threshold-dictionary';
import {ThresholdService} from '../../services/threshold.service';
import {CountedAndPaginated} from '../../interface/response/counted-and-paginated';

@Component({
  selector: 'app-dict-thresholds',
  templateUrl: './dict-thresholds.component.html',
  styleUrls: ['./dict-thresholds.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictThresholdsComponent)}
  ]
})
export class DictThresholdsComponent implements OnInit, ControlValueAccessor, OnDestroy, OnChanges {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control?: FormControl = null;
  @Input() required = false;
  @Input() appearance = null;
  @Input() insuranceCompanyId?: number = null;
  @Output() selectedDict: EventEmitter<ThresholdDictionary> = new EventEmitter<ThresholdDictionary>();
  @Output() selectedMultipleDict: EventEmitter<ThresholdDictionary[]> = new EventEmitter<ThresholdDictionary[]>();

  externalControl = true;
  disabled = false;
  items: ThresholdDictionary[] = [];
  filteredItems: ThresholdDictionary[] = null;
  value?: number = null;
  currentColor = '';
  filterControl: FormControl;
  itemsSubject: ReplaySubject<CountedAndPaginated<ThresholdDictionary>> = new ReplaySubject<CountedAndPaginated<ThresholdDictionary>>(1);

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private thresholdService: ThresholdService,
    private fb: FormBuilder,
  ) {
    this.filterControl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((thresholdDictionary: ThresholdDictionary) => {
            if (thresholdDictionary.uuid === val) {
              exists = true;
            }
          });

          if (!exists) {
            this.thresholdService.loadList(0, 9999, {
              in$uuid: val
            }, this.itemsSubject);
          }
        } else {
          this.loadItems();
        }
      });

    this.filterControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val?: string) => {
        if (!val) {
          this.filteredItems = null;
          return;
        }

        this.loadItems(val);
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: CountedAndPaginated<ThresholdDictionary>) => {
        this.items = result.items;
      });
  }

  ngOnChanges(): void {
  }

  loadItems(name?: string): void {
    this.items = [];
    const subject: ReplaySubject<CountedAndPaginated<ThresholdDictionary>> = new ReplaySubject(1);
    const filters: any = {};
    if (name) {
      filters.contains$name = name;
    } else {
      filters.in$uuid = this.control.value;
    }
    this.thresholdService.loadList(0, 20, filters, subject);

    subject
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<ThresholdDictionary>) => {
        if (name) {
          this.filteredItems = result.items.sort((a: ThresholdDictionary, b: ThresholdDictionary) => {
            return a.name.localeCompare(b.name);
          });
        } else {
          this.items = result.items.sort((a: ThresholdDictionary, b: ThresholdDictionary) => {
            return a.name.localeCompare(b.name);
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    if (this.multiple) {
      const selected = [];
      this.items.forEach((dict: ThresholdDictionary) => {
        if (e.value.includes(dict.uuid)) {
          selected.push(dict);
        }

        this.selectedMultipleDict.emit(selected);
      });
    } else {
      this.items.forEach((dict: ThresholdDictionary) => {
        if (dict.uuid === e.value) {
          this.selectedDict.emit(dict);
        }
      });
    }

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }
}
